import { Controller } from "@hotwired/stimulus"
import Swal from "sweetalert2"

const options = [
  ["payout_requests", "Host payout requests", "New Interac and PayPal requests"],
  ["payout_results", "Payout results & problems", "Payment confirmations and payments needing attention"],
  ["conversions", "Guests becoming hosts", "First parties created · 9 PM Toronto · skips zero"],
  ["reminders", "Birthday reminders", "People who saved a date · combined at 9 PM · skips zero"],
  ["morning", "Your morning summary", "Yesterday’s parties, funds contributed and closed-party gross fees · 8 AM Toronto"],
  ["balances", "Low provider balances", "Wise below $2,000 · Giftbit and Amazon below $750"]
]
const escape = (value) => String(value).replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]))

export default class extends Controller {
  static targets = ["bell", "badge"]
  static values = { enabled: Boolean, url: String, workerUrl: String, rememberUrl: String }

  connect() {
    const standalone = matchMedia("(display-mode: standalone)").matches || navigator.standalone === true
    const handheld = navigator.maxTouchPoints > 0 || matchMedia("(pointer: coarse)").matches
    this.active = this.enabledValue && standalone && handheld
    if (!this.active) return
    this.bellTarget.hidden = false
    this.supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
    this.visible = () => { if (!document.hidden) this.load(false).catch(() => {}) }
    this.message = (event) => { if (event.data?.type === "admin-notification") this.load(false).catch(() => {}) }
    document.addEventListener("visibilitychange", this.visible)
    navigator.serviceWorker?.addEventListener("message", this.message)
    this.load(true).catch(() => {})
  }

  disconnect() {
    this.active = false
    document.removeEventListener("visibilitychange", this.visible)
    navigator.serviceWorker?.removeEventListener("message", this.message)
    if (Swal.getPopup()?.classList.contains("admin-push-modal")) Swal.close()
    if (this.hasBellTarget) this.bellTarget.hidden = true
  }

  async load(prompt) {
    const data = await this.request("")
    if (!this.active) return
    this.notificationData = data
    this.badgeTarget.hidden = !data.unread
    this.badgeTarget.textContent = data.unread > 99 ? "99+" : data.unread
    this.bellTarget.setAttribute("aria-label", data.unread ? `Notifications, ${data.unread} unread` : "Notifications")
    if (prompt && !data.prompted && this.supported && !Swal.isVisible()) await this.settings(true)
  }

  async open() {
    if (!this.active) return
    try {
      await this.load(false)
      const rows = this.notificationData.notifications.map((item) => `<a class="admin-push-item ${item.read ? "" : "is-unread"}" href="${escape(item.path)}" data-notification-id="${item.id}"><strong>${escape(item.title)}</strong><span>${escape(item.body)}</span><small>${escape(new Date(item.created_at).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }))}</small></a>`).join("")
      const result = await Swal.fire({ title: "Notifications", customClass: { popup: "admin-push-modal" },
        html: `${!this.notificationData.device_enabled ? '<p class="admin-push-intro">Push notifications are off on this device. Open Settings to enable them.</p>' : ''}<div class="admin-push-inbox">${rows || '<p class="admin-push-empty">You’re all caught up. Your updates will appear here.</p>'}</div>`,
        showCloseButton: true, showDenyButton: true, confirmButtonText: "Settings", denyButtonText: "Mark all read",
        didOpen: (popup) => popup.querySelectorAll("[data-notification-id]").forEach((link) => link.addEventListener("click", (event) => {
          event.preventDefault()
          const target = new URL(link.href, location.href)
          if (target.origin !== location.origin || !target.pathname.startsWith("/admin/")) return
          this.request("/read", "POST", { id: link.dataset.notificationId }).catch(() => {}).finally(() => { location.assign(target.href) })
        })) })
      if (result.isConfirmed) await this.settings(false)
      if (result.isDenied) { await this.request("/read", "POST", {}); await this.load(false) }
    } catch (error) { this.showError(error) }
  }

  async settings(first) {
    if (!this.supported) {
      return Swal.fire({ icon: "info", title: "Push isn’t available on this device", text: "Use the installed app on a supported phone or tablet. On iPhone and iPad, use iOS 16.4 or later and open ECHOage from your Home Screen." })
    }
    // Prepare registration before the click; permission itself must start directly
    // inside that click, with no network await ahead of it (required by iOS).
    this.registration = await navigator.serviceWorker.register(this.workerUrlValue, { scope: "/admin/", updateViaCache: "none" })
    await this.waitForWorker(this.registration)
    this.subscription = await this.registration.pushManager.getSubscription()
    const onDevice = this.notificationData.device_enabled && this.subscription && Notification.permission === "granted"
    const selected = this.notificationData.categories
    const rows = options.map(([id, label, detail]) => `<label class="admin-push-option"><input type="checkbox" value="${id}" ${selected.includes(id) ? "checked" : ""}><span><strong>${label}</strong><small>${detail}</small></span></label>`).join("")
    const blocked = Notification.permission === "denied"
    // Persist that the explanation was shown, including dismissal. No repeated nag.
    if (first) await this.request("/preferences", "PATCH", { categories: selected })
    if (!this.active) return
    const result = await Swal.fire({ title: first ? "Stay in the loop" : "Notification settings",
      customClass: { popup: "admin-push-modal" },
      html: `<p class="admin-push-intro">Choose your ECHOage updates. These preferences apply across your installed devices.</p>${blocked ? '<p class="admin-push-warning">Notifications are blocked for this app. Enable them in your device settings, then return here.</p>' : ''}<div class="admin-push-options">${rows}</div><p class="admin-push-footnote">Balance checks run every 5 minutes and after recorded payouts. Amazon uses our recorded balance.</p>${onDevice ? '<button class="admin-push-test" type="button">Send a test notification</button><p class="admin-push-footnote" data-push-test-status role="status"></p>' : ''}`,
      confirmButtonText: onDevice || blocked ? "Save preferences" : "Enable notifications",
      showCancelButton: true, cancelButtonText: first ? "Not now" : "Close",
      showDenyButton: Boolean(onDevice), denyButtonText: "Turn off on this device",
      showLoaderOnConfirm: true, allowOutsideClick: () => !Swal.isLoading(),
      didOpen: (popup) => popup.querySelector(".admin-push-test")?.addEventListener("click", async (event) => {
        const button = event.currentTarget
        button.disabled = true
        const status = popup.querySelector("[data-push-test-status]")
        try {
          await this.request("/test", "POST", {})
          status.textContent = "Test queued to your enabled devices."
        } catch (error) { status.textContent = error.message }
        finally { button.disabled = false }
      }),
      preConfirm: () => {
        const categories = [...Swal.getPopup().querySelectorAll("input:checked")].map((input) => input.value)
        // Keep requestPermission in the synchronous click stack.
        const permission = Notification.permission === "default" && categories.length ? Notification.requestPermission() : Promise.resolve(Notification.permission)
        return this.enable(categories, permission).catch((error) => { Swal.showValidationMessage(error.message); return false })
      }
    })
    if (result.isDenied) {
      await this.request("/subscription", "DELETE", {})
      await this.subscription?.unsubscribe()
    }
    await this.load(false)
  }

  async enable(categories, permissionPromise) {
    const permission = await permissionPromise
    await this.request("/preferences", "PATCH", { categories })
    if (permission !== "granted" || !categories.length) return true
    // Establish the remembered PWA login before associating this device with it.
    const response = await fetch(this.rememberUrlValue, { method: "POST", credentials: "same-origin", cache: "no-store", headers: this.headers() })
    if (!response.ok || response.redirected) throw new Error("Please reopen the app and sign in again.")
    const remembered = await response.json()
    if (remembered.csrf_token) document.querySelector("meta[name='csrf-token']")?.setAttribute("content", remembered.csrf_token)
    let subscription = await this.registration.pushManager.getSubscription()
    const key = this.keyBytes(this.notificationData.public_key)
    if (subscription?.options?.applicationServerKey && !this.sameKey(subscription.options.applicationServerKey, key)) {
      await subscription.unsubscribe()
      subscription = null
    }
    subscription ||= await this.registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
    await this.request("/subscription", "POST", { subscription: subscription.toJSON() })
    return true
  }

  keyBytes(value) {
    const raw = atob(value.replace(/-/g, "+").replace(/_/g, "/"))
    return Uint8Array.from(raw, (char) => char.charCodeAt(0))
  }

  waitForWorker(registration) {
    const worker = registration.installing || registration.waiting || registration.active
    if (worker?.state === "activated") return Promise.resolve()
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => { worker?.removeEventListener("statechange", changed); reject(new Error("The app is updating. Please reopen it and try again.")) }, 15000)
      const changed = () => {
        if (worker.state === "activated") { clearTimeout(timer); worker.removeEventListener("statechange", changed); resolve() }
      }
      worker?.addEventListener("statechange", changed)
    })
  }

  sameKey(buffer, key) {
    const bytes = new Uint8Array(buffer)
    return bytes.length === key.length && bytes.every((value, index) => value === key[index])
  }

  headers() {
    return { "Accept": "application/json", "Content-Type": "application/json", "X-Admin-PWA": "standalone", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "" }
  }

  async request(path, method = "GET", data) {
    const response = await fetch(`${this.urlValue}${path}`, { method, headers: this.headers(), credentials: "same-origin", cache: "no-store", ...(data ? { body: JSON.stringify(data) } : {}) })
    const result = await response.json().catch(() => ({}))
    if (!response.ok || response.redirected) throw new Error(result.error || "Couldn’t load notifications. Check your connection and try again.")
    return result
  }

  showError(error) {
    if (this.active) Swal.fire({ icon: "error", title: "Notifications unavailable", text: error.message })
  }
}
